import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { useEscalations } from '../api/escalations';
import { useEngineerList } from '../api/engineers';
import PageHeader from '../components/layout/PageHeader';
import Badge from '../components/ui/Badge';
import { TablePageSkeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import ErrorState from '../components/ui/ErrorState';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDate } from '../lib/dates';
import type { EscalationLevel } from '../types/api';

const LEVEL_VARIANT: Record<EscalationLevel, 'yellow' | 'red'> = {
  TMinus3: 'yellow', TMinus1: 'red', Overdue: 'red',
};
const LEVEL_LABEL: Record<EscalationLevel, string> = {
  TMinus3: 'T-3', TMinus1: 'T-1', Overdue: 'Overdue',
};

export default function EscalationsPage() {
  const navigate = useNavigate();
  const { data: escalations, isLoading, error, refetch } = useEscalations();
  const { data: engineers } = useEngineerList();

  if (isLoading) return <TablePageSkeleton cols={5} hasFilters hasAction={false} />;
  if (error)     return <ErrorState onRetry={refetch} />;

  return (
    <div>
      <PageHeader title="Escalations" description="Tasks approaching or past their due date" />

      {escalations?.length === 0 ? (
        <Card>
          <EmptyState
            icon={CheckCircle2}
            title="All tasks on track"
            description="No active escalations right now."
            className="[&_svg]:text-emerald-500"
          />
        </Card>
      ) : (
        <Card>
          <div className="flex items-center border-b px-4 py-2.5">
            <span className="text-xs text-muted-foreground">
              {escalations?.length} active escalation{escalations?.length !== 1 ? 's' : ''}
            </span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Task</TableHead>
                <TableHead>Assignee</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Days</TableHead>
                <TableHead>Level</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {escalations?.map((esc) => {
                const eng = engineers?.find((e) => e.id === esc.assigneeId);
                return (
                  <TableRow key={esc.taskId} className="cursor-pointer" onClick={() => navigate(`/tasks/${esc.taskId}`)}>
                    <TableCell>
                      <Link to={`/tasks/${esc.taskId}`} className="font-medium text-foreground hover:text-primary" onClick={(e) => e.stopPropagation()}>
                        {esc.taskTitle}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{eng?.name ?? 'Unassigned'}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(esc.dueDate)}</TableCell>
                    <TableCell>
                      {esc.daysUntilDue < 0
                        ? <span className="text-destructive font-medium">{Math.abs(esc.daysUntilDue)}d late</span>
                        : <span className="text-muted-foreground">{esc.daysUntilDue}d</span>
                      }
                    </TableCell>
                    <TableCell>
                      <Badge label={LEVEL_LABEL[esc.level]} variant={LEVEL_VARIANT[esc.level]} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
